import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../utils/api'

// Analytics Operating Workspace store (BUI-F-17).
//
// Owns the async lifecycle for the workspace panel, the drill-down detail
// panel (loaded on demand and isolated from the primary workspace — US-64
// AC10), and the recommendation-draft save flow. Two hardening invariants live
// here rather than in the view:
//   • newest-response-wins: each fetch carries a monotonic sequence; a slower
//     earlier response is dropped so it cannot clobber a newer one (US-64 AC1).
//   • error classification: transport/timeout/server failures map to
//     actionable, non-raw messages (US-64 AC9).
export const useAnalyticsWorkspaceStore = defineStore('analyticsWorkspace', () => {
  const workspace = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const errorKind = ref(null) // 'timeout' | 'network' | 'server' | 'generic'

  // Drill-down detail panel — deliberately separate state so its loading/error
  // never blocks the main workspace.
  const drilldown = ref(null)
  const drilldownMetric = ref(null)
  const drilldownLoading = ref(false)
  const drilldownError = ref(null)

  // Recommendation-draft save flow.
  const saving = ref(false)
  const saveError = ref(null)
  const saveFieldErrors = ref([])
  const saveSuccess = ref(false)

  // aria-live announcement text for assistive tech (US-64 AC8).
  const announce = ref('')

  let workspaceSeq = 0
  let drilldownSeq = 0

  const permissions = computed(() => workspace.value?.permissions || { canView: true, canManage: false, canVerify: false })
  const cards = computed(() => (workspace.value?.cards || []).filter((c) => c && c.visible !== false))
  const anomalies = computed(() => workspace.value?.anomalies || [])
  const actions = computed(() => workspace.value?.actions || [])
  const freshness = computed(() => workspace.value?.freshness || null)
  // Untrusted data (stale / degraded) disables destructive controls (US-64 AC7).
  const dataTrusted = computed(() => !!freshness.value && freshness.value.stale !== true)

  // Single explicit state discriminator — exactly one of these renders
  // (US-60 AC2 / US-64 AC2).
  const uiState = computed(() => {
    if (loading.value) return 'loading'
    if (error.value) return 'error'
    if (!workspace.value) return 'empty'
    if (!cards.value.length && !anomalies.value.length && !actions.value.length) return 'empty'
    return 'ready'
  })

  function classifyError(err) {
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
      return { kind: 'timeout', message: 'The request timed out. Check your connection and retry.' }
    }
    if (!err?.response) {
      return { kind: 'network', message: 'Network error — you appear to be offline. Retry when reconnected.' }
    }
    if (err.response.status >= 500) {
      return { kind: 'server', message: 'Analytics is temporarily unavailable. Please retry shortly.' }
    }
    return { kind: 'generic', message: err.response?.data?.error || 'Something went wrong. Please retry.' }
  }

  async function loadWorkspace(siteId) {
    const seq = ++workspaceSeq
    loading.value = true
    error.value = null
    errorKind.value = null
    try {
      const { data } = await api.get(`/sites/${siteId}/analytics/workspace`)
      if (seq !== workspaceSeq) return // stale response — newest wins
      workspace.value = data.workspace || null
      announce.value = 'Analytics workspace updated'
    } catch (err) {
      if (seq !== workspaceSeq) return
      const c = classifyError(err)
      errorKind.value = c.kind
      error.value = c.message
      announce.value = 'Failed to load analytics workspace'
    } finally {
      if (seq === workspaceSeq) loading.value = false
    }
  }

  async function loadDrilldown(siteId, metricKey) {
    const seq = ++drilldownSeq
    drilldownMetric.value = metricKey
    drilldownLoading.value = true
    drilldownError.value = null
    // Reset save state when opening a new drill-down.
    saveError.value = null
    saveFieldErrors.value = []
    saveSuccess.value = false
    try {
      const { data } = await api.get(`/sites/${siteId}/analytics/workspace/drilldown/${metricKey}`)
      if (seq !== drilldownSeq) return
      drilldown.value = data.drilldown || null
      announce.value = `Drill-down opened for ${data.drilldown?.context?.metricName || metricKey}`
    } catch (err) {
      if (seq !== drilldownSeq) return
      drilldownError.value = classifyError(err).message
    } finally {
      if (seq === drilldownSeq) drilldownLoading.value = false
    }
  }

  function closeDrilldown() {
    drilldown.value = null
    drilldownMetric.value = null
    drilldownError.value = null
  }

  // Save a recommendation draft (queue a new action). Retains form input on
  // failure (US-62 AC10 / US-64 AC5) by never clearing the caller's model here.
  async function queueAction(siteId, payload) {
    saving.value = true
    saveError.value = null
    saveFieldErrors.value = []
    saveSuccess.value = false
    try {
      const { data } = await api.post(`/sites/${siteId}/analytics/workspace/actions`, payload)
      saveSuccess.value = true
      announce.value = 'Action queued'
      await loadWorkspace(siteId)
      return data.action
    } catch (err) {
      saveFieldErrors.value = err?.response?.data?.details?.fieldErrors || []
      saveError.value = saveFieldErrors.value.length ? 'Please fix the highlighted fields.' : classifyError(err).message
      announce.value = 'Could not queue action'
      return null
    } finally {
      saving.value = false
    }
  }

  async function updateAction(siteId, actionId, patch) {
    saving.value = true
    saveError.value = null
    saveFieldErrors.value = []
    try {
      const { data } = await api.patch(`/sites/${siteId}/analytics/workspace/actions/${actionId}`, patch)
      announce.value = 'Action updated'
      await loadWorkspace(siteId)
      return data.action
    } catch (err) {
      saveFieldErrors.value = err?.response?.data?.details?.fieldErrors || []
      saveError.value = classifyError(err).message
      return null
    } finally {
      saving.value = false
    }
  }

  async function verifyAction(siteId, actionId, body) {
    saving.value = true
    saveError.value = null
    try {
      const { data } = await api.post(`/sites/${siteId}/analytics/workspace/actions/${actionId}/verify`, body || {})
      announce.value = `Action ${data.action?.verification?.result || 'verified'}`
      await loadWorkspace(siteId)
      return data.action
    } catch (err) {
      saveError.value = classifyError(err).message
      return null
    } finally {
      saving.value = false
    }
  }

  async function saveConfig(siteId, patch) {
    try {
      await api.patch(`/sites/${siteId}/analytics/workspace/config`, patch)
      announce.value = 'Card configuration saved'
      await loadWorkspace(siteId)
      return true
    } catch (err) {
      error.value = classifyError(err).message
      return false
    }
  }

  return {
    workspace, loading, error, errorKind,
    drilldown, drilldownMetric, drilldownLoading, drilldownError,
    saving, saveError, saveFieldErrors, saveSuccess, announce,
    permissions, cards, anomalies, actions, freshness, dataTrusted, uiState,
    loadWorkspace, loadDrilldown, closeDrilldown,
    queueAction, updateAction, verifyAction, saveConfig,
  }
})
